// Compact serialization of the simulator scenario into a URL-hash payload.
// The full input state is base64url-encoded so shareable links are ~200 chars
// and require no backend storage.

const PKG = ["bronze", "silver", "gold", "platinum", "diamond"];
const pkgIdx = (id) => Math.max(0, PKG.indexOf(id));
const pkgById = (i) => PKG[i] ?? PKG[0];

// Layout: [
//   version,
//   myPackageIdx,
//   directs, directPkgIdx,
//   l2Per, l2PkgIdx, l3Per, l3PkgIdx, ..., l10Per, l10PkgIdx,
//   downlinePkgIdx,
//   duplicationPct,
//   dupMult*100, branch*10, retention*100,
//   configuredLevels,
//   calculateDeeper ? 1 : 0
// ]
const V = 1;

const b64urlEncode = (obj) => {
  const json = JSON.stringify(obj);
  const b64 = typeof btoa === "function"
    ? btoa(unescape(encodeURIComponent(json)))
    : Buffer.from(json, "utf-8").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const b64urlDecode = (str) => {
  const b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const json = typeof atob === "function"
    ? decodeURIComponent(escape(atob(b64 + pad)))
    : Buffer.from(b64 + pad, "base64").toString("utf-8");
  return JSON.parse(json);
};

const round = (n, d = 2) => Math.round(n * 10 ** d) / 10 ** d;

export function encodeScenario({ inputs, configuredLevels, calculateDeeper }) {
  const payload = [
    V,
    pkgIdx(inputs.myPackage),
    inputs.directs, pkgIdx(inputs.directPackage),
    inputs.l2PerDirect, pkgIdx(inputs.l2Package),
    inputs.l3PerL2,    pkgIdx(inputs.l3Package),
    inputs.l4PerL3,    pkgIdx(inputs.l4Package),
    inputs.l5PerL4,    pkgIdx(inputs.l5Package),
    inputs.l6PerL5,    pkgIdx(inputs.l6Package),
    inputs.l7PerL6,    pkgIdx(inputs.l7Package),
    inputs.l8PerL7,    pkgIdx(inputs.l8Package),
    inputs.l9PerL8,    pkgIdx(inputs.l9Package),
    inputs.l10PerL9,   pkgIdx(inputs.l10Package),
    pkgIdx(inputs.downlinePackage),
    Math.round(inputs.duplicationPct),
    Math.round(inputs.duplicationMultiplier * 100),
    Math.round(inputs.branchingFactor * 10),
    Math.round(inputs.retentionRate * 100),
    configuredLevels,
    calculateDeeper ? 1 : 0,
  ];
  return b64urlEncode(payload);
}

export function decodeScenario(str) {
  if (!str) return null;
  let a;
  try {
    a = b64urlDecode(str);
  } catch {
    return null;
  }
  if (!Array.isArray(a) || a[0] !== V) return null;
  try {
    let i = 1;
    const inputs = {
      myPackage:        pkgById(a[i++]),
      directs:          a[i++],
      directPackage:    pkgById(a[i++]),
      l2PerDirect:      a[i++], l2Package: pkgById(a[i++]),
      l3PerL2:          a[i++], l3Package: pkgById(a[i++]),
      l4PerL3:          a[i++], l4Package: pkgById(a[i++]),
      l5PerL4:          a[i++], l5Package: pkgById(a[i++]),
      l6PerL5:          a[i++], l6Package: pkgById(a[i++]),
      l7PerL6:          a[i++], l7Package: pkgById(a[i++]),
      l8PerL7:          a[i++], l8Package: pkgById(a[i++]),
      l9PerL8:          a[i++], l9Package: pkgById(a[i++]),
      l10PerL9:         a[i++], l10Package: pkgById(a[i++]),
      downlinePackage:  pkgById(a[i++]),
      duplicationPct:   a[i++],
      duplicationMultiplier: round(a[i++] / 100, 2),
      branchingFactor:  round(a[i++] / 10, 1),
      retentionRate:    round(a[i++] / 100, 2),
    };
    const configuredLevels = a[i++];
    const calculateDeeper = !!a[i++];
    return { inputs, configuredLevels, calculateDeeper };
  } catch {
    return null;
  }
}

export function buildShareUrl(payloadStr) {
  const url = new URL(window.location.href);
  // Point to the backend /api/share/:payload route which serves rich OG meta
  // tags for social scrapers and redirects real browsers to the SPA with the
  // scenario restored in the URL hash.
  url.pathname = `/api/share/${payloadStr}`;
  url.hash = "";
  url.search = "";
  return url.toString();
}

export function readScenarioFromHash() {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  const match = hash.match(/(?:^#|&)s=([^&]+)/);
  if (!match) return null;
  return decodeScenario(match[1]);
}
